import { useEffect, useState } from "react";

/**
 * Tracks a CSS media query from JavaScript.
 *
 * Almost everything responsive here is a Tailwind variant, which needs no JS.
 * This exists for the two cases CSS cannot express: setting `role` on a stacked
 * table (see `Common/Table/StackedTable`), and rendering the bottom tab bar's
 * markup only where it applies.
 *
 * Returns `false` during the first render on the server or in a test without
 * `matchMedia`, then corrects on mount — so callers must tolerate a false start
 * rather than branching on it for anything destructive.
 */
export const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;

    const list = window.matchMedia(query);
    setMatches(list.matches);

    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
    list.addEventListener("change", onChange);
    return () => list.removeEventListener("change", onChange);
  }, [query]);

  return matches;
};

/**
 * Below Tailwind's `md`. The .98 avoids a dead pixel against `min-width: 768px`.
 */
export const useIsMobile = (): boolean =>
  useMediaQuery("(max-width: 767.98px)");

export default useMediaQuery;
